if (!customElements.get('product-card-media-slider')) {
  class ProductCardMediaSlider extends HTMLElement {
    // Time the cursor has to rest on a card before the images start cycling.
    // Long enough not to trigger while scrolling past, short enough to feel snappy.
    static HOVER_DELAY = 1000;

    constructor() {
      super();
      this.initialized = false;
      this.slider = null;
      this.isHovering = false;
    }

    connectedCallback() {
      // Use Intersection Observer for lazy initialization
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && !this.initialized) {
                this.initSlider();
                this.observer.unobserve(this);
              }
            });
          },
          {
            rootMargin: '200px',
            threshold: 0
          }
        );
        this.observer.observe(this);
      } else {
        this.initSlider();
      }
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }
      if (this.slider) {
        this.slider.destroy(true, true);
      }
      this.removeEventListeners();
    }

    initSlider() {
      if (this.initialized) return;

      const swiperContainer = this.querySelector('.swiper');
      if (!swiperContainer) return;

      const slideCount = this.querySelectorAll('.swiper-slide').length;
      if (slideCount <= 1) return;

      // Get autoplay speed from data attribute (in seconds), convert to milliseconds
      const autoplaySpeed = parseFloat(this.dataset.autoplaySpeed) || 3;
      const autoplayDelay = autoplaySpeed * 1000;

      // Product cards may sit inside swipeable carousels on mobile: there the
      // media stays anchored to its card and the parent slider handles drags.
      // In plain grids (collection, search) the images can be swiped.
      const insideCarousel = !!(this.parentElement && this.parentElement.closest('.swiper'));

      this.sliderOptions = {
        a11y: false,
        threshold: 5,
        slidesPerView: 1,
        spaceBetween: 0,
        loop: true,
        watchOverflow: true,
        allowTouchMove: !insideCarousel,
        simulateTouch: false,
        autoplay: {
          delay: autoplayDelay,
          disableOnInteraction: false,
          pauseOnMouseEnter: false
        },
        on: {
          init: () => {
            this.initialized = true;
          }
        }
      };

      this.slider = new Swiper(swiperContainer, this.sliderOptions);

      // Stop autoplay initially - only run on hover (desktop)
      this.slider.autoplay.stop();

      this.setupEventListeners();
    }

    setupEventListeners() {
      // Desktop: start autoplay once the cursor rested on the card, stop on leave
      this.handleMouseEnter = () => {
        this.isHovering = true;
        if (!this.slider || window.innerWidth < 750) return;

        clearTimeout(this.hoverTimer);
        this.hoverTimer = setTimeout(() => {
          if (!this.isHovering || !this.slider) return;
          // Start with an animated transition, then keep autoplaying.
          this.slider.slideNext(this.slider.params.speed || 300);
          this.slider.autoplay.start();
        }, ProductCardMediaSlider.HOVER_DELAY);
      };

      this.handleMouseLeave = () => {
        this.isHovering = false;
        clearTimeout(this.hoverTimer);
        if (this.slider) {
          this.slider.autoplay.stop();
          // Reset to first slide when mouse leaves
          this.slider.slideTo(0, 300);
        }
      };

      this.addEventListener('mouseenter', this.handleMouseEnter);
      this.addEventListener('mouseleave', this.handleMouseLeave);
    }

    removeEventListeners() {
      clearTimeout(this.hoverTimer);
      this.removeEventListener('mouseenter', this.handleMouseEnter);
      this.removeEventListener('mouseleave', this.handleMouseLeave);
    }

    /**
     * Public method to navigate to a specific slide
     * @param {number} index - Zero-based slide index
     */
    goToMedia(index) {
      if (this.slider && typeof index === 'number') {
        this.slider.slideTo(index);
      }
    }

    /**
     * Get the current active slide index
     * @returns {number} Current slide index
     */
    getCurrentIndex() {
      return this.slider ? this.slider.realIndex : 0;
    }
  }

  customElements.define('product-card-media-slider', ProductCardMediaSlider);
}
